import { supabase } from '../lib/supabase';

export const teamService = {
  async getUserTeams(userId) {
    try {
      const { data, error } = await supabase?.rpc('get_user_teams', {
        p_user_id: userId
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getTeamMembers(teamId) {
    try {
      const { data, error } = await supabase?.rpc('get_team_members_with_activity', {
        p_team_id: teamId
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async checkSeatAvailability(teamId) {
    try {
      const { data, error } = await supabase?.rpc('check_team_seat_availability', {
        p_team_id: teamId
      });

      if (error) throw error;
      return { data: data?.[0] || null, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createTeamInvitation(teamId, email, role, invitedBy) {
    try {
      // Generate invitation token
      const invitationToken = Array.from(crypto.getRandomValues(new Uint8Array(32)))?.map(b => b?.toString(16)?.padStart(2, '0'))?.join('');

      // Create invitation in database
      const { data: invitation, error: dbError } = await supabase
        ?.from('team_invitations')
        ?.insert({
          team_id: teamId,
          invited_by: invitedBy,
          email: email,
          role: role,
          invitation_token: invitationToken,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString(),
          status: 'pending'
        })
        ?.select()
        ?.single();

      if (dbError) throw dbError;

      // Get team details for email
      const { data: team } = await supabase
        ?.from('teams')
        ?.select('name')
        ?.eq('id', teamId)
        ?.single();

      // Get inviter details
      const { data: inviter } = await supabase
        ?.from('user_profiles')
        ?.select('full_name')
        ?.eq('id', invitedBy)
        ?.single();

      // Send invitation email
      const { error: emailError } = await supabase?.functions?.invoke('send-team-invitation', {
        body: {
          teamId,
          invitedBy,
          email,
          role,
          invitationToken,
          teamName: team?.name,
          inviterName: inviter?.full_name
        }
      });

      if (emailError) {
        console.error('Failed to send invitation email:', emailError);
      }

      return { data: invitation, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async acceptInvitation(invitationToken, userId) {
    try {
      // Get invitation details
      const { data: invitation, error: invError } = await supabase
        ?.from('team_invitations')
        ?.select('*, teams(max_seats)')
        ?.eq('invitation_token', invitationToken)
        ?.eq('status', 'pending')
        ?.single();

      if (invError) throw invError;
      if (!invitation) throw new Error('Invalid or expired invitation');

      // Check if invitation is expired
      if (new Date(invitation?.expires_at) < new Date()) {
        throw new Error('Invitation has expired');
      }

      // Check seat availability
      const { data: seatCheck } = await this.checkSeatAvailability(invitation?.team_id);
      if (!seatCheck?.can_add_member) {
        throw new Error('Team has reached maximum seat capacity');
      }

      // Add user to team
      const { error: memberError } = await supabase
        ?.from('team_members')
        ?.insert({
          team_id: invitation?.team_id,
          user_id: userId,
          role: invitation?.role
        });

      if (memberError) throw memberError;

      // Update invitation status
      const { error: updateError } = await supabase
        ?.from('team_invitations')
        ?.update({
          status: 'accepted',
          accepted_at: new Date()?.toISOString()
        })
        ?.eq('id', invitation?.id);

      if (updateError) throw updateError;

      return { data: { teamId: invitation?.team_id }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getPendingInvitations(userId) {
    try {
      // Get user email
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('email')
        ?.eq('id', userId)
        ?.single();

      if (!profile?.email) {
        return { data: [], error: null };
      }

      const { data, error } = await supabase
        ?.from('team_invitations')
        ?.select(`
          *,
          teams(name, avatar_url),
          invited_by_profile:user_profiles!team_invitations_invited_by_fkey(full_name)
        `)
        ?.eq('email', profile?.email)
        ?.eq('status', 'pending')
        ?.gt('expires_at', new Date()?.toISOString());

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async switchTeamContext(userId, teamId) {
    try {
      // Update last_active_at for the team
      const { error } = await supabase
        ?.from('team_members')
        ?.update({ last_active_at: new Date()?.toISOString() })
        ?.eq('user_id', userId)
        ?.eq('team_id', teamId);

      if (error) throw error;
      return { data: { success: true }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async removeMember(teamId, userId) {
    try {
      const { error } = await supabase
        ?.from('team_members')
        ?.delete()
        ?.eq('team_id', teamId)
        ?.eq('user_id', userId);

      if (error) throw error;
      return { data: { success: true }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateMemberRole(teamId, userId, newRole) {
    try {
      const { error } = await supabase
        ?.from('team_members')
        ?.update({ role: newRole })
        ?.eq('team_id', teamId)
        ?.eq('user_id', userId);

      if (error) throw error;
      return { data: { success: true }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getTeamUsageStats(teamId) {
    try {
      const { data, error } = await supabase
        ?.from('agent_usage_metrics')
        ?.select('*')
        ?.eq('team_id', teamId);

      if (error) throw error;

      // Aggregate stats
      const stats = {
        totalInteractions: 0,
        totalTasks: 0,
        totalContent: 0,
        byAgent: {}
      };

      data?.forEach(metric => {
        if (metric?.metric_type === 'interaction') {
          stats.totalInteractions += Number(metric?.metric_value || 0);
        } else if (metric?.metric_type === 'task_completed') {
          stats.totalTasks += Number(metric?.metric_value || 0);
        } else if (metric?.metric_type === 'content_generated') {
          stats.totalContent += Number(metric?.metric_value || 0);
        }

        const agentId = metric?.agent_id;
        if (!stats?.byAgent?.[agentId]) {
          stats.byAgent[agentId] = { interactions: 0, tasks: 0, content: 0 };
        }

        if (metric?.metric_type === 'interaction') {
          stats.byAgent[agentId].interactions += Number(metric?.metric_value || 0);
        } else if (metric?.metric_type === 'task_completed') {
          stats.byAgent[agentId].tasks += Number(metric?.metric_value || 0);
        } else if (metric?.metric_type === 'content_generated') {
          stats.byAgent[agentId].content += Number(metric?.metric_value || 0);
        }
      });

      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};